import * as React from 'react';
import type {ReactNode} from 'react';
import {SquareX} from 'lucide-react';
import {SelectTrigger} from '../SelectTrigger';
import type {ControlProps} from './types';
import type {SelectValue} from '../types';

/**
 * Static (read-only) trigger button used when `searchPosition === 'dropdown'`,
 * which keeps the search input inside the menu instead of the trigger. Ignores
 * the input/keyboard props from ControlProps — those are routed through the
 * in-menu search input.
 */
function StaticControl<T, V extends SelectValue, L extends ReactNode>(
  props: ControlProps<T, V, L>,
) {
  const {
    triggerId,
    listboxId,
    labelId,
    valueId,
    describedErrorId,
    accessibleLabel,
    isOpen,
    isBlocked,
    hasValue,
    invalid,
    loading,
    disabled,
    valueLabel,
    placeholder,
    showPlaceholderWhenIdle,
    onContainerClick,
    onTriggerFocus,
    onClear,
    clearable,
    clearLabel,
    hideIndicator,
    readOnly,
    leftIcon,
  } = props;
  const showClear = Boolean(clearable) && hasValue && !readOnly && !disabled;

  return (
    <div className="relative">
      <SelectTrigger
        triggerId={triggerId}
        listboxId={listboxId}
        labelId={labelId}
        valueId={valueId}
        describedErrorId={describedErrorId}
        accessibleLabel={accessibleLabel}
        hasValue={hasValue}
        isOpen={isOpen}
        isBlocked={isBlocked}
        disabled={disabled}
        readOnly={readOnly}
        loading={loading}
        invalid={invalid}
        placeholder={placeholder}
        showPlaceholderWhenIdle={showPlaceholderWhenIdle}
        valueLabel={valueLabel}
        leftIcon={leftIcon}
        hideIndicator={hideIndicator}
        onClick={onContainerClick}
        onKeyDown={() => undefined}
        onFocus={onTriggerFocus}
      />
      {showClear && (
        <button
          type="button"
          onClick={onClear}
          onMouseDown={event => event.stopPropagation()}
          className="absolute right-9 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-[3px] border-0 bg-transparent p-0 text-[var(--chekin-neutral-500)] hover:shadow-[0_3px_3px_#0f477734]"
          aria-label={clearLabel}
        >
          <SquareX
            size={15}
            fill="var(--chekin-neutral-500)"
            color="#f8f8f8"
            strokeWidth={1.8}
          />
        </button>
      )}
    </div>
  );
}

export {StaticControl};
